import { useCrossSellTracker } from "@packages/polling-checker"
import { Badge, BlockStack, Box, Button, Card, InlineStack, SkeletonBodyText, Text, Tooltip } from "@shopify/polaris"
import { ClipboardIcon, EditIcon } from "@shopify/polaris-icons"
import copy from "copy-to-clipboard"
import dayjs from "dayjs"
import { get, isEmpty } from "lodash-es"
import { Observer } from "mobx-react"
import React, { useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

import OrdersAPI from "@/api/orders"
import { ITrackingResultItem } from "@/api/TP2/tracking-info"
import { hasPermission } from "@/component"
import PermissionBadge from "@/component/PermissionBadge"
import { ToProtection } from "@/component/Protection"
import TagList from "@/component/TagList/TagList"
import { CROSS_SELL_POINT_ENUM } from "@/enums/CrossSellPoint"
import { OrderSyncToPayPal } from "@/enums/order-status"
import Permission from "@/enums/permission"
import { Mode } from "@/models/IOrderTagsManageStore"
import AddTagInput from "@/pages/Orders/components/OrderInfo/AddTagInput"
import CourierInfo from "@/pages/Orders/components/OrderInfo/CourierInfo/CourierInfo"
import LastMileCourierInfo from "@/pages/Orders/components/OrderInfo/CourierInfo/LastMileCourierInfo"
import InfoItem from "@/pages/Orders/components/OrderInfo/InfoItem/InfoItem"
import useOrderCustomerInfo from "@/pages/Orders/components/OrderInfo/useOrderCustomerInfo"
import ShipmentsProducts from "@/pages/Orders/components/ShipmentsProducts"
import { SubStatus, SubStatusData } from "@/pages/Orders/OrderStatusTypes"
import stores from "@/stores"
import { ADMIN_SCOPED_SHIPMENT_INFO_FIELDS, maskOrderWordInT } from "@/utils/customerAuthGate"
import { DateFormats, formatDate } from "@/utils/datetime"
import { formatPhoneNumber } from "@/utils/phone"
import { navigateToCustomerById, navigateToOrderById } from "@/utils/redirectShopify"
import toast from "@/utils/toast"

// edd 来源的枚举
export enum ProviderSourceEnum
{
  ETA = "ETA",
  SHOPIFY = "SHOPIFY",
  CARRIER = "CARRIER",
  CUSTOM = "CUSTOM",
}

interface ShipmentInfoProps{
  tracking?: ITrackingResultItem
  changeLoading?: boolean
}
export default function ShipmentInfo({
  tracking,
  changeLoading,
}:ShipmentInfoProps){
  const {
    ordersStore: { orderInfoDataLoadedTime, orderInfoData },
    userInfoSore,
  } = stores

  const { t: rawT, i18n } = useTranslation("orders")
  const { t: commonT } = useTranslation("common")
  const { t: protectionT } = useTranslation("delivery_guarantee")

  // 超管后台 + 客户未授权（白名单模式）：只保留 ADMIN_SCOPED_SHIPMENT_INFO_FIELDS 里的字段，文案里的 Order(s) 换成 ORD
  const { isAdminScoped } = userInfoSore
  const t = maskOrderWordInT(rawT, isAdminScoped)

  // 白名单模式下 Shipping protection 那一块不展示，也就不用上报曝光
  useCrossSellTracker(() => window.CrossSellTracker?.logViewed(CROSS_SELL_POINT_ENUM.OrdersDetailProtection), !isAdminScoped && hasPermission(Permission.ParcelWILLShippingNotifications) && userInfoSore.data.is_pi_eligible && !userInfoSore.data.is_pi_active)

  // 可见性监听 - 当 ToProtection 元素可见时触发
  // const { ref: toProtectionRef } = useSmartVisibleCrossSellTracker({
  //   onViewed: () => {
  //     window.CrossSellTracker?.logViewed(CROSS_SELL_POINT_ENUM.OrdersDetailProtection)
  //   },
  // })
  const orderDate = get(tracking, "order_created_at", "-")
  const fulfilledAt = get(tracking, "order_fulfilled_at")
  const location = get(tracking, "location")
  const pickupDate = get(tracking, "pickup_date")
  const deliveryDate = get(tracking, "delivery_date")
  const transitDay = get(tracking, "transitDay")
  const residenceDay = get(tracking, "residenceDay")
  // 时长由后端统一取整，前端只展示；已用 ?? 把缺键统一成 null；0 是有效值，不能用真值判空
  const processingTime = tracking?.processing_time ?? null
  const pickupTime = tracking?.pickup_time ?? null
  const orderToDeliveryTime = tracking?.order_to_delivery_time ?? null
  const formatDuration = (days: number | null) => days === null ? "-" : t("OrderInfo.nDay", { count: days })
  const shippingTimeCon = get(tracking, "shipping_time_con")
  // Customer / Contact information / Shipping address 优先取 Shopify 的实时数据，取不到时回退到后端字段
  // 白名单模式下这几块不展示，不用请求
  const { info: shopifyOrder, loading: shopifyOrderLoading } = useOrderCustomerInfo(get(orderInfoData, "order_id"), !isAdminScoped, window.shopify?.config?.locale || i18n.language)
  // 拿到 Shopify 数据后整体以它为准（客户没填的字段就显示 -），不再逐字段混用后端可能过时的值
  const shopifyCustomer = shopifyOrder?.customer
  const customerID = shopifyOrder
    ? shopifyCustomer?.id.split("/").pop()
    : get(orderInfoData, "customer_id")
  const customerName = shopifyOrder ? shopifyCustomer?.displayName : get(orderInfoData, "customer_name", "-")
  const rawPhoneNumber = shopifyOrder ? shopifyOrder.phone : get(orderInfoData, "phone_number")
  const phoneNumber = formatPhoneNumber(rawPhoneNumber)
  const customerEmail = shopifyOrder ? shopifyOrder.email : get(orderInfoData, "email", "-")
  const shippingAddress = shopifyOrder?.shippingAddress
  const shippingAddressPhone = formatPhoneNumber(shippingAddress?.phone)
  // 与 Shopify 订单详情一致：姓名、地址各段（省份用缩写，如 TX）、电话逐行展示
  const shippingAddressLines = [
    shippingAddress?.name,
    ...(shippingAddress?.formatted || []),
    shippingAddressPhone,
  ].filter(Boolean) as string[]
  // 复制内容与 Shopify 一致：省份、国家换成 Shopify 后台语言的全称（如 Texas），各段与电话用逗号连成一行
  const shippingAddressCopyText = shippingAddress
    ? [
      ...(shopifyOrder?.shippingAddressCopyLines || []),
      formatPhoneNumber(shippingAddress.phone, { forCopy: true }),
    ].filter(Boolean).join(", ")
    : orderInfoData?.shipping_address
  const trackingNumber = get(tracking, "track_number") as string
  const lastMileCarrier = get(tracking, "last_mile_carrier")
  const lastMileTrackingSupported = get(tracking, "last_mile_tracking_supported")
  const dataLoading = orderInfoDataLoadedTime === 0

  useEffect(() => {
    userInfoSore.data.timezone && dayjs.tz.setDefault(userInfoSore.data.timezone)
  }, [])

  // 根据状态ID获取Value
  const getSubStatusWordById = (id: SubStatus) => {
    const target = SubStatusData[id]

    return target ? t(target.il8nKey as any) : ""
  }

  const navigator = useNavigate()
  const dataList: { key: string, filed: string, value: React.ReactNode }[] = [
    {
      key: "orderNumber",
      filed: t("OrderNumber"),
      value: <Button
        onClick={() => navigateToOrderById(String(get(orderInfoData, "order_id")))}
        variant="plain"
      >{get(orderInfoData, "order", "-")}</Button>,
    },
    {
      key: "orderDate",
      filed: t("OrderDate"),
      value: orderDate ? formatDate(orderDate, DateFormats.MONTH_DAY_TIME) : "-",
    },
    {
      key: "location",
      filed: t("Location"),
      value: location?.name || "-",
    },
    {
      key: "fulfillmentDate",
      filed: t("FulfillmentDate"),
      value: fulfilledAt ? formatDate(fulfilledAt, DateFormats.MONTH_DAY_TIME) : "-",
    },
    {
      key: "pickupDate",
      filed: t("PickupDate"),
      value: pickupDate ? formatDate(pickupDate, DateFormats.MONTH_DAY_TIME) : "-",
    },
    {
      key: "deliveryDate",
      filed: t("DeliveryDate"),
      value: deliveryDate ? formatDate(deliveryDate, DateFormats.MONTH_DAY_TIME) : "-",
    },
    {
      key: "shipmentStatus",
      filed: t("ShipmentStatus"),
      value: changeLoading
        ? <div className={"w-10 h-5 flex items-center"}><SkeletonBodyText lines={1} /></div>
        : get(tracking, "status", "-") || "-",
    },
    {
      key: "shipmentSubStatus",
      filed: t("ShipmentSubStatusLabel"),
      value: changeLoading
        ? <div className={"w-10 h-5 flex items-center"}><SkeletonBodyText lines={1} /></div>
        : tracking ? getSubStatusWordById(tracking.substatus_value) : "-",
    },
    {
      key: "processingTime",
      filed: t("ProcessingTime"),
      value: formatDuration(processingTime),
    },
    {
      key: "pickupTime",
      filed: t("PickupTime"),
      value: formatDuration(pickupTime),
    },
    {
      key: "transitTime",
      filed: t("TransitTime"),
      value: transitDay ? t("OrderInfo.nDay", { count: transitDay }) : "-",
    },
    {
      key: "orderToDeliveryTime",
      filed: t("OrderToDeliveryTime"),
      value: formatDuration(orderToDeliveryTime),
    },
    {
      key: "residenceTime",
      filed: t("ResidenceTime"),
      value: residenceDay ? t("OrderInfo.nDay", { count: residenceDay }) : "-",
    },
  ]

  // 连接paypal后展示详情的订单同步状态
  if (stores.userInfoSore.data.is_paypal_connected && !isAdminScoped) {
    // @ts-ignore
    const status = get<ITrackingResultItem>(tracking, "paypalSyncStatus")
    let statusValue = "-"

    switch (status) {
      case OrderSyncToPayPal.OtherPaymentMethods:
        statusValue = t("OtherPaymentMethods")
        break
      case OrderSyncToPayPal.SyncedSuccessfully:
        statusValue = t("SyncedSuccessfully")
        break
      case OrderSyncToPayPal.SyncFailed:
        statusValue = t("SyncFailed")
        break
      case OrderSyncToPayPal.Unsynced:
        statusValue = t("Unsynced")
        break
    }

    dataList.push({
      key: "paypalSyncStatus",
      filed: t("PayPalSyncStatus"),
      value: statusValue,
    })
  }

  const loading = <BlockStack gap="400">{
    new Array(3).fill("")
      .map((_, index) => <Card key={index}><SkeletonBodyText lines={12} /></Card>)
  }</BlockStack>

  const ProviderSourceMap: Record<ProviderSourceEnum, string> = {
    [ProviderSourceEnum.ETA]: t("SourcedFromETA"),
    [ProviderSourceEnum.SHOPIFY]: t("SourcedFromShopify"),
    [ProviderSourceEnum.CARRIER]: t("SourcedFromCarrier"),
    [ProviderSourceEnum.CUSTOM]: t("SourcedFromCustom"),
  }


  const statusMap = {
    1: {
      tone: "attention",
      trans_key: "CoverStatus.Pending",
    },
    2: {
      tone: "success",
      trans_key: "CoverStatus.Active",
    },
    3: {
      tone: "enabled",
      trans_key: "CoverStatus.Cancelled",
    },
    4: {
      tone: "enabled",
      trans_key: "CoverStatus.Expired",
    },
  } as const

  const info = statusMap[orderInfoData?.coverInfo?.shippingProtectionStatus as "1"]

  return (
    <Observer>{() => dataLoading
      ? loading
      : <BlockStack gap="400">
        <Card>
          <BlockStack gap="400">
            {/* ShipmentInfo */}
            <BlockStack gap="200">
              <Text as="h2" variant="headingSm">
                {t("ShipmentInfo")}
              </Text>

              <div className={"py-2 flex flex-col gap-3"}>
                {!isEmpty(trackingNumber) ? (
                  <CourierInfo
                    courierName={get(tracking, "carrier_name") as string}
                    courierURL={get(tracking, "url") as string}
                    courierLogoURL={get(tracking, "img") as string}
                    trackingNumber={trackingNumber}
                    copyTrackingNumberTooltip={t("CopyTrackingNumber")}
                    onCopyTrackingNumber={() => toast(commonT("CopiedSuccessfully"))}
                  />
                ) : (<div>-</div>)}

                {lastMileTrackingSupported && lastMileCarrier ? (
                  <LastMileCourierInfo
                    courierName={lastMileCarrier.name}
                    courierURL={lastMileCarrier.redirect_link}
                    courierLogoURL={lastMileCarrier.logo_url}
                    trackingNumber={lastMileCarrier.tracking_number}
                    copyTrackingNumberTooltip={t("CopyLastMileTrackingNumber")}
                    onCopyTrackingNumber={() => toast(commonT("CopiedSuccessfully"))}
                  />
                ) : null}
              </div>

              {(isAdminScoped
                ? dataList.filter(({ key }) => ADMIN_SCOPED_SHIPMENT_INFO_FIELDS.includes(key))
                : dataList
              ).map(({ key, filed, value }) => (
                <InfoItem key={key} field={filed} content={value} />
              ))}
            </BlockStack>

            {/* EDD、Shipping protection：白名单模式（超管后台 + 客户未授权）下不展示 */}
            {!isAdminScoped && (
              <>
                {/* EstimatedDeliveryTime */}
                <BlockStack gap="200">
                  <InlineStack>
                    <Text as="h2" variant="headingSm">
                      <div className={"flex items-center"}>
                        {t("EstimatedDeliveryTime")}
                        {!hasPermission(Permission.EstimatedDeliveryTime)
                        && <div style={{ display: "inline-block" }}>
                          <PermissionBadge text={"Essential"} level={2} permission={Permission.EstimatedDeliveryTime} />
                        </div>}
                      </div>
                    </Text>
                  </InlineStack>

                  <div className={"flex flex-col gap-1"}>
                    {shippingTimeCon ? (
                      <>
                        <Text as="p" variant="bodyMd" fontWeight="regular">
                          {shippingTimeCon || "-"}
                        </Text>

                        <Text as="p" variant="bodyMd" tone={"subdued"}>
                          {tracking ? ProviderSourceMap[tracking.edd.source] : ""}
                        </Text>
                      </>
                    ) : (
                      <Text as="p" variant="bodyMd">-</Text>
                    )}
                  </div>
                </BlockStack>
                {/* Shipping protection */}
                <BlockStack gap="200">
                  <InlineStack>
                    <Text as="h2" variant="headingSm">
                      <div className={"flex items-center "}>
                        <div>
                          {t("ShippingProtection")}
                        </div>
                        <div>
                          {
                            info ?
                              <div className="ml-2"><Badge tone={info.tone}>{protectionT(info.trans_key)}</Badge></div>
                              : !hasPermission(Permission.ParcelWILLShippingNotifications) ? <PermissionBadge text={"Essential"} level={2} permission={Permission.ParcelWILLShippingNotifications} />
                                : (userInfoSore.data.is_pi_eligible && !userInfoSore.data.is_pi_active) ? <ToProtection CrossSellTrackerPoint={CROSS_SELL_POINT_ENUM.OrdersDetailProtection} />
                                  : null
                          }
                        </div>
                      </div>
                    </Text>
                  </InlineStack>
                  <div>
                    {
                      orderInfoData && orderInfoData.coverInfo?.contractId ? (
                        <Button variant="plain" onClick={() => {
                          navigator({
                            pathname: "/protection/covers",
                            search: `?coverId=${orderInfoData.coverInfo?.contractId}`,
                          })
                        }}
                        >
                          {orderInfoData?.coverInfo.contractId}
                        </Button>
                      ) : "-"
                    }

                  </div>
                </BlockStack>
              </>
            )}
          </BlockStack>
        </Card>

        {/* Customer / Tags / Shipment items：白名单模式下不展示 */}
        {!isAdminScoped && (
          <>
            <Card>
              <BlockStack gap="400">
                <BlockStack gap="200">
                  <Text as="h2" variant="headingSm">
                    {t("Customer")}
                  </Text>
                  {shopifyOrderLoading ? <SkeletonBodyText lines={1} /> : (
                    <Text as="p" variant="bodyMd" fontWeight="regular">
                      {customerID && customerName ?
                        (<Button
                          variant="plain" target="_blank"
                          onClick={() => navigateToCustomerById(String(customerID))}
                        >{customerName}</Button>)
                        : customerName || "-"
                      }
                    </Text>
                  )}
                </BlockStack>

                <BlockStack gap="200">
                  <Text as="h2" variant="headingSm">
                    {t("ContactInformation")}
                  </Text>

                  {shopifyOrderLoading ? <SkeletonBodyText lines={2} /> : <div className={"flex flex-col gap-1"}>
                    <InfoItem
                      field={customerEmail || "-"}
                      fieldTone={"base"}
                      content={!isEmpty(customerEmail) ? (
                        <Tooltip content={t("CopyEmail")}>
                          <Button variant="plain" size={"slim"} icon={ClipboardIcon} onClick={() => {
                            copy(String(customerEmail))
                            toast(commonT("CopiedSuccessfully"))
                          }}
                          />
                        </Tooltip>
                      ) : (<></>)}
                    />

                    <InfoItem
                      field={phoneNumber || "-"}
                      fieldTone={"base"}
                      content={!isEmpty(phoneNumber) ? (
                        <Tooltip content={t("CopyPhoneNumber")}>
                          <Button variant="plain" size={"slim"} icon={ClipboardIcon} onClick={() => {
                            copy(formatPhoneNumber(rawPhoneNumber, { forCopy: true }))
                            toast(commonT("CopiedSuccessfully"))
                          }}
                          />
                        </Tooltip>
                      ) : (<></>)}
                    />
                  </div>}
                </BlockStack>

                <BlockStack gap="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingSm">
                      {t("ShippingAddress")}
                    </Text>

                    {/* Tooltip 外层的 span 里按钮按行内元素排在文字基线上，行高会多撑出一截导致偏离居中，行高置 0 让高度只由按钮决定 */}
                    {!shopifyOrderLoading && !isEmpty(shippingAddressCopyText) && (
                      <div style={{ lineHeight: 0 }}>
                        <Tooltip content={t("CopyAddress")}>
                          <Button variant="plain" size={"slim"} icon={ClipboardIcon} onClick={() => {
                            copy(String(shippingAddressCopyText))
                            toast(commonT("CopiedSuccessfully"))
                          }}
                          />
                        </Tooltip>
                      </div>
                    )}
                  </InlineStack>

                  {shopifyOrderLoading ? <SkeletonBodyText lines={4} /> : shippingAddressLines.length ? (
                    <BlockStack>
                      {shippingAddressLines.map((line, index) => (
                        <Text key={index} breakWord as="p" variant="bodyMd">{line}</Text>
                      ))}
                    </BlockStack>
                  ) : (
                    <Text breakWord as="p" variant="bodyMd">
                      {orderInfoData?.shipping_address || "-"}
                    </Text>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingSm">
                    {t("Tags")}
                  </Text>

                  <Button variant="tertiary" icon={EditIcon} onClick={() => {
                    stores.orderTagsManageStore.mode = Mode.ADD_TAGS
                    stores.orderTagsManageStore.shipmentCount = 1
                    stores.orderTagsManageStore.shipmentSelectedItems = [`${tracking?.nid}`]
                    stores.orderTagsManageStore.queryParams = undefined
                    stores.orderTagsManageStore.setOpen(true, true)
                  }}
                  />
                </InlineStack>

                <AddTagInput numberID={tracking?.nid} />

                <OrderTags numberID={tracking?.nid} />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingSm">
                    {t("ShipmentItems")}
                  </Text>
                </InlineStack>

                <Box paddingBlockStart="200">
                  {
                    tracking
                      ? <ShipmentsProducts products={tracking.products} />
                      : <SkeletonBodyText lines={3} />
                  }
                </Box>
              </BlockStack>
            </Card>
          </>
        )}
      </BlockStack>
    }</Observer>
  )
}

function OrderTags({ numberID }: { numberID: number|null|undefined }) {
  return (<Observer>{() => {
    const { orderTags } = stores.ordersStore.orderInfoData || {}

    const handleRemove = useCallback((tag: string) => {
      OrdersAPI
        .manageOrderTags({
          action: "REMOVE",
          number_ids: [numberID],
          tags: [tag],
        }, "REMOVE")
        .then(({ data }) => {
          if (data.code === 200) {
            stores.ordersStore.removeTagsToShipments([tag], [])
          }
        })
    }, [numberID])

    return !orderTags?.length || !numberID ? (<></>) : (
      <TagList tags={orderTags} onRemove={handleRemove} />
    )
  }}</Observer>)
}
